import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import { FaEnvelope, FaLock, FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';

function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (!token) {
            setError('Invalid password reset link. No token provided.');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (!token) {
            setError('Missing reset token.');
            return;
        }
        if (password.length < 8) {
             setError('Password must be at least 8 characters long.');
             return;
        }

        setIsLoading(true);
        setMessage('');
        setError('');

        try {
            const response = await fetch(`${apiUrl}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: token, email: email, new_password: password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || `HTTP error! status: ${response.status}`);
            }

            setMessage(data.message || 'Password reset successfully!');
            setPassword('');
            setConfirmPassword('');

            const userIp = await fetch('https://api.ipify.org?format=json')
                .then(response => response.json())
                .then(data => data.ip)
                .catch(() => 'unknown IP');

            const passwordChangeRequestDetails = {
                log_description: `User has successfully changed their password at ${new Date()} from IP address: ${userIp}`,
                user_username: email,
            };

            await fetch(`${apiUrl}/logs/`, {   
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(passwordChangeRequestDetails),
            });

            setTimeout(() => navigate('/'), 3000);

        } catch (err) {
            console.error("Reset Password Error:", err);
             setError(err.message.includes("Failed to fetch") ? "Network error." : (err.message || 'Failed to reset password. The link may be invalid or expired.'));
             
            const userIp = await fetch('https://api.ipify.org?format=json')
                .then(response => response.json())
                .then(data => data.ip)
                .catch(() => 'unknown IP');

            const passwordChangeRequestDetails = {
                log_description: `User has failed to change their password at ${new Date()} from IP address: ${userIp}`,
                user_username: email,
            };

            await fetch(`${apiUrl}/logs/`, {   
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(passwordChangeRequestDetails),
            });

        } finally {
            setIsLoading(false);
        }
    };

    if (!token && !error) {
        return (
            <div className="auth-page-container">
                <div className="loading-state">
                    <p className="form-feedback error">Invalid or missing password reset link.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page-container">
            <header className="auth-header">
                <button onClick={() => navigate('/')} className="back-button">
                    <FaArrowLeft /> Back to Login
                </button>
                <img src={logo} alt="Wysupp Logo" className="header-logo" />
            </header>

            <main className="auth-card-wrapper">
                <h1 className="card-title">Reset Your Password</h1>
                
                {message ? (
                    <div className="form-feedback success">{message}</div>
                ) : (
                    <form onSubmit={handleSubmit} className="auth-form">
                        {error && <div className="form-feedback error">{error}</div>}
                        
                        <div className="input-group">
                            <FaEnvelope className="input-icon" />
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="Enter your email"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="input-group">
                            <FaLock className="input-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength="8"
                                placeholder="New password (min. 8 chars)"
                                disabled={isLoading}
                            />
                            {showPassword ? (
                                <FaEyeSlash className="password-toggle-icon" onClick={() => setShowPassword(false)} />
                            ) : (
                                <FaEye className="password-toggle-icon" onClick={() => setShowPassword(true)} />
                            )}
                        </div>
                        <div className="input-group">
                            <FaLock className="input-icon" />
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                placeholder="Confirm new password"
                                disabled={isLoading}
                            />
                             {showConfirmPassword ? (
                                <FaEyeSlash className="password-toggle-icon" onClick={() => setShowConfirmPassword(false)} />
                            ) : (
                                <FaEye className="password-toggle-icon" onClick={() => setShowConfirmPassword(true)} />
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="action-button"
                        >
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>
                )}
            </main>
            <style>{`
                .auth-page-container, .auth-page-container * {
                    box-sizing: border-box;
                }

                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0;
                    --light-text: #a0aec0; --green-accent: #48bb78; --border-color: #4a5568;
                    --input-bg: #1a202c; --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                }
                .auth-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                }
                .auth-header {
                    width: 100%;
                    max-width: 500px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .header-logo { height: 30px; }
                .back-button {
                    background: transparent; color: var(--light-text); border: none; padding: 0;
                    font-weight: 600; cursor: pointer; transition: color 0.2s;
                    display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { color: var(--text-color); }
                
                .auth-card-wrapper {
                    width: 100%;
                    max-width: 500px;
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 2.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .card-title {
                    font-size: 1.75rem; font-weight: 700;
                    color: var(--text-color); margin-bottom: 2rem;
                }
                
                .auth-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .form-feedback {
                    width: 100%; padding: 0.75rem; border-radius: 6px;
                    font-size: 0.9rem; font-weight: 500;
                }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }

                .input-group { position: relative; }
                .input-group input {
                    width: 100%;
                    height: 2.5rem; /* FIX 2: Added for consistent input height. */
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }
                .input-group input:focus {
                    outline: none;
                    border-color: var(--green-accent);
                }
                
                .input-icon {
                    position: absolute;
                    top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                }
                
                .password-toggle-icon {
                    position: absolute;
                    top: 50%;
                    right: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                    cursor: pointer;
                }

                .action-button {
                    width: 100%;
                    background-color: var(--green-accent); color: var(--dark-bg);
                    border: none; border-radius: 6px; padding: 0.8rem;
                    font-size: 1rem; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; margin-top: 0.5rem;
                }
                .action-button:hover:not(:disabled) { background-color: #38a169; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }

                .input-group input:-webkit-autofill ~ .password-toggle-icon,
                .input-group input:autofill ~ .password-toggle-icon {
                color: var(--dark-bg); /* Change to a dark color */
                }

                /* Optional: Adjust hover color when autofilled */
                .input-group input:-webkit-autofill ~ .password-toggle-icon:hover,
                .input-group input:autofill ~ .password-toggle-icon:hover {
                color: var(--card-bg); /* Use a slightly lighter dark color on hover */
                }

                .loading-state { text-align: center; color: var(--text-color); }
            `}</style>
        </div>
    );
}

export default ResetPassword;